import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Player } from '../../types';
import { resizeImage } from '../../utils/imageUtils';

// Interface for group members with role information
export interface GroupMember extends Player {
  role: 'admin' | 'member' | 'pending' | 'rejected';
}

export interface Group {
  id: string;
  name: string;
  avatar?: string;
  created_by: string;
  created_at: string;
  members?: any[];
  status?: string;
  is_public?: boolean;
  city?: string;    // Make sure this is defined
  state?: string;   // Make sure this is defined
  pendingCount?: number;
  userMembershipStatus?: 'admin' | 'member' | 'pending' | 'rejected' | null;
}

export const useGroupManagement = (currentUser: Player, onDataRefresh?: () => void) => {
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [groupRequestStatus, setGroupRequestStatus] = useState<{ [key: string]: boolean }>({});

  const fetchGroups = async () => {
    try {
      // Get groups and members
      const { data: groups, error } = await supabase
        .from('groups')
        .select(`
          *,
          group_members!inner (
            user_id,
            role
          ),
          members:group_members(user_id, role),
          pending_count:group_members(count)
        `)
        .eq('group_members.user_id', currentUser.id);
      
      if (error) throw error;
      
      // For each group where user is admin, get pending count
      const groupsWithPending = await Promise.all((groups || []).map(async group => {
        const isAdmin = group.members.find(m => 
          m.user_id === currentUser.id && 
          (m.role === 'admin' || group.created_by === currentUser.id)
        );
      
        let pendingCount = 0;
        if (isAdmin) {
          const { count, error: countError } = await supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id)
            .eq('role', 'pending');
            
          if (!countError) {
            pendingCount = count || 0;
          }
        }
      
        return {
          id: group.id,
          name: group.name,
          avatar: group.avatar,
          created_by: group.created_by,
          created_at: group.created_at,
          members: group.members || [],
          status: group.members.find(m => m.user_id === currentUser.id)?.role || 'member',
          pendingCount,
          city: group.city,        // Add this
          state: group.state,      // Add this
          is_public: group.is_public
        };
      }));
      
      return groupsWithPending;
    } catch (error) {
      console.error('Error fetching groups:', error);
      return [];
    }
  };

  const handleFileChange = async (file: File) => {
    if (file.size > 3 * 1024 * 1024) {
      throw new Error('A imagem deve ter no máximo 3MB');
    }

    try {
      return await resizeImage(file);
    } catch (err) {
      throw new Error('Erro ao processar a imagem');
    }
  };

  const searchPlayers = async (term: string) => {
    if (term.length < 3) return [];

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .ilike('name', `%${term}%`)
        .limit(5);

      if (error) throw error;
      console.log('Raw search data from Supabase:', data); // Debug log
      return data || [];
    } catch (error) {
      console.error('Error searching players:', error);
      return [];
    }
  };

  const createGroup = async (groupData: {
    name: string;
    avatar?: string;
    isPublic: boolean;
    members: Player[];
    city: string;     // Add this
    state: string;    // Add this
  }) => {
    if (!groupData.name.trim()) {
      throw new Error('Por favor, insira um nome para o grupo');
    }
  
    if (!groupData.city.trim() || !groupData.state.trim()) {
      throw new Error('Por favor, insira a cidade e estado do grupo');
    }
  
    try {
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .insert([{
          name: groupData.name,
          avatar: groupData.avatar,
          created_by: currentUser.id,
          is_public: groupData.isPublic,
          city: groupData.city,        // Add this
          state: groupData.state       // Add this
        }])
        .select()
        .single();
  
      if (groupError) throw groupError;
  
      const memberInserts = [
        ...groupData.members.map(member => ({
          group_id: group.id,
          user_id: member.id,
          role: 'member'
        })),
        {
          group_id: group.id,
          user_id: currentUser.id,
          role: 'admin'
        }
      ];
  
      const { error: membersError } = await supabase
        .from('group_members')
        .insert(memberInserts);
  
      if (membersError) throw membersError;
  
      return group;
    } catch (error) {
      throw error;
    }
  };

  const fetchGroupMembers = async (groupId: string) => {
    try {
      const { data: memberData, error: memberError } = await supabase
        .from('group_members')
        .select('user_id, role')
        .eq('group_id', groupId);

      if (memberError) throw memberError;

      if (memberData && memberData.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name, avatar, phone')
          .in('id', memberData.map(member => member.user_id));

        if (profilesError) throw profilesError;
        
        const membersWithRoles: GroupMember[] = profilesData?.map(profile => ({
          ...profile,
          email: '', // Add required fields from Player interface
          password: '',
          playing_side: 'both' as const,
          gender: 'male' as const,
          requiredCategories: [],
          role: memberData.find(m => m.user_id === profile.id)?.role as 'admin' | 'member' | 'pending' | 'rejected'
        })) || [];
        
        setGroupMembers(membersWithRoles);
        return membersWithRoles;
      }
      
      setGroupMembers([]);
      return [];
    } catch (error) {
      console.error('Error fetching group members:', error);
      return [];
    }
  };

  const editGroup = async (groupId: string, updateData: {
    name: string;
    avatar?: string;
    isPublic: boolean;
    newMembers: Player[];
    city: string;
    state: string;
  }) => {
    try {
      if (!updateData.name.trim()) {
        throw new Error('O nome do grupo não pode ficar vazio');
      }

      // Check if user has permission to edit the group
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('created_by, is_public')
        .eq('id', groupId)
        .single();

      if (groupError) throw groupError;

      // Check if user is admin or creator
      const { data: memberData, error: memberError } = await supabase
        .from('group_members')
        .select('role')
        .eq('group_id', groupId)
        .eq('user_id', currentUser.id)
        .single();

      const isCreator = groupData.created_by === currentUser.id;
      const isAdmin = memberData?.role === 'admin';

      if (!isCreator && !isAdmin) {
        throw new Error('Você não tem permissão para editar este grupo');
      }

      // If trying to change visibility and user is not creator or admin, prevent it
      if (updateData.isPublic !== groupData.is_public && !isCreator && !isAdmin) {
        throw new Error('Apenas administradores podem alterar a visibilidade do grupo');
      }

      const updateFields: any = {
        name: updateData.name.trim(),
        city: updateData.city,
        state: updateData.state
      };

      // Only allow avatar and visibility changes for admins/creators
      if (isCreator || isAdmin) {
        updateFields.avatar = updateData.avatar;
        updateFields.is_public = updateData.isPublic;
      }
  
      const { data, error: updateError } = await supabase
        .from('groups')
        .update(updateFields)
        .eq('id', groupId)
        .select();
  
      if (updateError) throw updateError;
  
      if (updateData.newMembers.length > 0) {
        const memberInserts = updateData.newMembers.map(member => ({
          group_id: groupId,
          user_id: member.id,
          role: 'member'
        }));
  
        const { error: insertError } = await supabase
          .from('group_members')
          .insert(memberInserts);
  
        if (insertError) throw insertError;
      }
  
      return data;
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  };

  const removeMember = async (groupId: string, memberId: string) => {
    try {
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', memberId);

      if (error) throw error;
      await fetchGroupMembers(groupId);
      
      // Trigger data refresh if callback is provided
      if (onDataRefresh) {
        onDataRefresh();
      }
    } catch (error) {
      throw error;
    }
  };

  const assignAdmin = async (groupId: string, memberId: string) => {
    try {
      const { error } = await supabase
        .from('group_members')
        .update({ role: 'admin' })
        .eq('group_id', groupId)
        .eq('user_id', memberId);

      if (error) throw error;
      await fetchGroupMembers(groupId);
    } catch (error) {
      throw error;
    }
  };

  const fetchPendingRequests = async (groupId: string) => {
    try {
      // First, get pending members
      const { data: pendingMembers, error: pendingError } = await supabase
        .from('group_members')
        .select('id, user_id, role')
        .eq('group_id', groupId)
        .eq('role', 'pending');
    
      if (pendingError) throw pendingError;
    
      if (pendingMembers && pendingMembers.length > 0) {
        // Then, get their profile information
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name, avatar, phone')
          .in('id', pendingMembers.map(member => member.user_id));
    
        if (profilesError) throw profilesError;
    
        // Combine the data
        const pendingRequestsWithProfiles = pendingMembers.map(member => ({
          id: member.id,
          user_id: member.user_id,
          role: member.role,
          profiles: profilesData?.find(profile => profile.id === member.user_id)
        }));
    
        setPendingRequests(pendingRequestsWithProfiles);
        return pendingRequestsWithProfiles;
      }
    
      setPendingRequests([]);
      return [];
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      return [];
    }
  };

  const handleJoinGroup = async (group: Group) => {
    try {
      // First, check if user is already a member regardless of group type
      const { data: existingMember, error: checkError } = await supabase
        .from('group_members')
        .select('role')
        .eq('user_id', currentUser.id)
        .eq('group_id', group.id)
        .single();

      // If no error and data exists, user is already associated with this group
      if (!checkError && existingMember) {
        const status = existingMember.role;
        if (status === 'member' || status === 'admin') {
          return { success: false, message: 'Você já é membro deste grupo.' };
        } else if (status === 'pending') {
          return { success: false, message: 'Você já tem uma solicitação pendente para este grupo.' };
        } else if (status === 'rejected') {
          return { success: false, message: 'Sua solicitação anterior foi recusada.' };
        }
      }

      if (group.is_public) {
        const { error } = await supabase
          .from('group_members')
          .insert({
            user_id: currentUser.id,
            group_id: group.id,
            role: 'member'
          });
  
        if (error) throw error;
        return { success: true, message: 'Você entrou no grupo com sucesso!', isPublic: true };
      } else {
        // Create new pending request for private groups
        const { error } = await supabase
          .from('group_members')
          .insert({
            user_id: currentUser.id,
            group_id: group.id,
            role: 'pending'
          });
  
        if (error) throw error;
  
        // Update UI state immediately
        setGroupRequestStatus(prev => ({ ...prev, [group.id]: true }));
        return { success: true, message: 'Solicitação enviada! Aguarde a aprovação do administrador.', isPublic: false };
      }
    } catch (error) {
      console.error('Error joining group:', error);
      return { success: false, message: 'Erro ao processar a solicitação.' };
    }
  };

  const handleRequestResponse = async (requestId: string, status: 'approved' | 'rejected', userId: string, groupId: string) => {
    try {
      // Simply update the role based on the status
      const { error: updateError } = await supabase
        .from('group_members')
        .update({ role: status === 'approved' ? 'member' : 'rejected' })
        .eq('id', requestId);
  
      if (updateError) throw updateError;
  
      // Refresh the lists
      await Promise.all([
        fetchPendingRequests(groupId),
        fetchGroupMembers(groupId)
      ]);
    } catch (error) {
      console.error('Error handling request response:', error);
      throw error;
    }
  };

  const checkUserMembership = async (groupId: string) => {
    try {
      const { data: membership, error } = await supabase
        .from('group_members')
        .select('role')
        .eq('user_id', currentUser.id)
        .eq('group_id', groupId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw error;
      }

      return membership ? membership.role : null;
    } catch (error) {
      console.error('Error checking user membership:', error);
      return null;
    }
  };

  const searchGroups = async (term: string) => {
    if (term.length < 3) return [];
    
    try {
      const { data, error } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          avatar,
          is_public,
          created_by,
          created_at,
          city,
          state
        `)
        .ilike('name', `%${term}%`)
        .limit(5);

      if (error) {
        console.error('Supabase search error:', error);
        throw error;
      }
      
      // Check membership status for each group
      const groupsWithMembership = await Promise.all(
        (data || []).map(async (group) => {
          const membershipStatus = await checkUserMembership(group.id);
          return {
            ...group,
            userMembershipStatus: membershipStatus
          };
        })
      );
      
      return groupsWithMembership;
    } catch (error) {
      console.error('Error searching groups:', error);
      return [];
    }
  };

  return {
    groupMembers,
    pendingRequests,
    groupRequestStatus,
    fetchGroups,
    handleFileChange,
    searchPlayers,
    createGroup,
    fetchGroupMembers,
    editGroup,
    removeMember,
    assignAdmin,
    fetchPendingRequests,
    handleJoinGroup,
    handleRequestResponse,
    searchGroups,
    checkUserMembership
  };
};